import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import apiService from 'services/api'
import { CompanyPageAccordion } from './CompanyPageAccordion'
import { DealCard } from './DealCard'
import { EditDealModal } from 'components/Deals/EditDealModal/EditDealModal'
import CreateDealModal from 'components/Deals/CreateDealModal/CreateDealModal'
import { DealsModal } from './Modals/DealsModal/DealsModal'
import { Deal } from 'models/deals'
import { TableTypeSlug, CompanyTypeSlug } from 'models/companies'
import { Plus } from 'lucide-react'

interface DealsOverviewAccordionProps {
  chainProxyId?: number
  doorProxyId?: number
  tableTypeSlug: TableTypeSlug
  companyTypeSlug: CompanyTypeSlug
  isChainPage?: boolean
}

export function DealsOverviewAccordion({
  chainProxyId,
  doorProxyId,
  tableTypeSlug,
  companyTypeSlug,
  isChainPage = false,
}: DealsOverviewAccordionProps) {
  const api = apiService()
  const queryClient = useQueryClient()
  const [dealIdToEdit, setDealIdToEdit] = useState<number>()
  const [showCreateDeal, setShowCreateDeal] = useState(false)
  const [showDealsModal, setShowDealsModal] = useState(false)

  // Determine which company ID to use
  const contactCompanyId = chainProxyId || doorProxyId

  // Fetch contact company data for the modal
  const { data: contactCompany } = api.useGetCompany(contactCompanyId as number)

  const params = {
    ...(doorProxyId
      ? { door_proxy_id: doorProxyId, door_only: true }
      : { company: contactCompanyId }),
    page_size: 50,
    include_ltv: true,
  }

  // Fetch deals for the company or door
  // When on a door page, only show door-specific deals (not company-wide deals)
  // Include LTV data for company/door page views (predicted revenue/volume)
  const [{ data: dealsResponse, isLoading: dealsLoading }, queryKey] =
    api.useGetDeals(params)

  // Fetch sales stages
  const { data: salesStages } = api.useGetSalesStages()

  // Fetch users for account owner names
  const { data: users } = api.useGetUsers()

  const deals = dealsResponse?.results || []

  // Handle deal edit
  const handleEditDeal = (deal: Deal) => {
    setDealIdToEdit(deal.id)
  }

  // Handle deal created - close modal and invalidate queries
  const handleDealCreated = () => {
    setShowCreateDeal(false)
    queryClient.invalidateQueries({ queryKey: queryKey })
  }

  return (
    <>
      <CompanyPageAccordion
        title={`DEALS (${deals.length})`}
        hideChevron={true}
        headerRight={
          <div className="flex items-center gap-3">
            {/* All Deals button - using ActionButton pattern from CompanyTopCard */}
            {isChainPage && (
              <button
                onClick={() => setShowDealsModal(true)}
                className="flex items-center justify-center gap-2 px-3 py-[6px] rounded-md border-[1px] border-[#2C6B4E] bg-[#EEF5F2] h-8 hover:bg-[#E5F2EA] transition-colors"
              >
                <span className="font-medium text-sm leading-none text-[#06512F]">
                  All Deals
                </span>
                <span className="font-light text-sm leading-none text-center text-[#06512F]">
                  ({deals.length})
                </span>
              </button>
            )}

            {/* Add deal button */}
            <button
              onClick={() => setShowCreateDeal(true)}
              className="flex h-8 w-8 items-center justify-center rounded border border-[#E2E2E2] bg-[#2C6B4E] transition-colors hover:bg-[#245A42]"
              aria-label="Add deal"
            >
              <Plus className="h-4 w-4 text-white" strokeWidth={2} />
            </button>
          </div>
        }
      >
        <div className="pt-4">
          {dealsLoading ? (
            <div className="text-center py-8 text-gray-500">
              Loading deals...
            </div>
          ) : deals.length === 0 ? (
            <div className="w-full rounded-lg border border-[#E2E2E2] bg-[#E2E2E2] pl-2">
              {/* Main content - white background with right-rounded corners */}
              <div className="bg-white rounded-r-lg p-3">
                <div className="flex items-center justify-between gap-2.5">
                  {/* Text content */}
                  <div className="flex flex-col gap-1">
                    <h3
                      className="text-base font-semibold uppercase text-[#191D23]"
                      style={{ lineHeight: '1.2em' }}
                    >
                      No Deals Yet.
                    </h3>
                    <p
                      className="text-xs font-light text-[#191D23]"
                      style={{ lineHeight: '1.2em' }}
                    >
                      Ready to make a deal?
                    </p>
                  </div>

                  {/* Add deal button */}
                  <button
                    onClick={() => setShowCreateDeal(true)}
                    className="flex h-8 items-center gap-2 rounded-md border border-[#2C6B4E] bg-[#EEF5F2] px-3 transition-colors hover:bg-[#DDE9E4]"
                  >
                    <Plus className="h-4 w-4 text-[#06512F]" strokeWidth={2} />
                    <span
                      className="text-sm font-medium text-[#06512F]"
                      style={{ lineHeight: '1.2em' }}
                    >
                      Add a deal
                    </span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              {/* Carousel/Scrollable container */}
              <div className="overflow-x-auto pb-2">
                <div className="flex gap-4 min-w-max">
                  {deals.map((deal) => (
                    <div key={deal.id} className="flex-shrink-0">
                      <DealCard
                        deal={deal}
                        salesStages={salesStages}
                        users={users}
                        onEdit={handleEditDeal}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Scroll hint if there are multiple deals */}
              {deals.length > 1 && (
                <div className="text-xs text-gray-400 text-center mt-2">
                  Scroll horizontally to see all deals →
                </div>
              )}
            </div>
          )}
        </div>
      </CompanyPageAccordion>

      {/* Edit Deal Modal */}
      <EditDealModal
        onClose={() => setDealIdToEdit(undefined)}
        dealsTableQueryKey={queryKey}
        show={!!dealIdToEdit}
        dealId={dealIdToEdit}
      />

      {/* Create Deal Modal */}
      {showCreateDeal && (
        <CreateDealModal
          show={showCreateDeal}
          handleClose={() => setShowCreateDeal(false)}
          targetCompanyId={chainProxyId}
          targetDoorProxyId={doorProxyId}
          forDoorSelection={!!doorProxyId}
          tableTypeSlug={tableTypeSlug}
          companyTypeSlug={companyTypeSlug}
          totalSelectedRows={1}
          optimisticUiTarget="deals-section"
          onDealCreated={handleDealCreated}
        />
      )}

      {/* Deals Modal - shows all deals in a table */}
      {contactCompany && (
        <DealsModal
          show={showDealsModal}
          onHide={() => setShowDealsModal(false)}
          contactCompany={contactCompany}
          forDoorProxyId={doorProxyId}
          companyName={contactCompany.name}
        />
      )}
    </>
  )
}
